import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Alert, AlertTitle, Button, Box, IconButton } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { fetchUsageAlerts, acknowledgeAlert } from "../../redux/slices/usageSlice";

/**
 * Severity based on threshold percentage.
 */
function getSeverity(thresholdPct) {
  if (thresholdPct >= 90) return "error";
  return "warning";
}

/**
 * Human-readable resource names.
 */
const RESOURCE_LABELS = {
  sites: "Sites",
  apps: "Docker Apps",
  storage: "Storage",
  bandwidth: "Bandwidth",
};

/**
 * UsageAlertBanner — Renders active usage alerts as dismissable banners.
 * Should be placed in the main layout, above page content.
 */
export default function UsageAlertBanner() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { alerts } = useSelector((state) => state.usage);

  useEffect(() => {
    dispatch(fetchUsageAlerts());
  }, [dispatch]);

  if (!alerts || alerts.length === 0) return null;

  const handleDismiss = (alertId) => {
    dispatch(acknowledgeAlert(alertId));
  };

  return (
    <Box sx={{ mb: 1 }}>
      {alerts.map((alert) => {
        const severity = getSeverity(alert.thresholdPct);
        const label = RESOURCE_LABELS[alert.resourceType] || alert.resourceType;
        const pct = alert.limitValue > 0
          ? Math.round((Number(alert.currentUsage) / Number(alert.limitValue)) * 100)
          : 0;

        return (
          <Alert
            key={alert.id}
            severity={severity}
            sx={{ mb: 0.5 }}
            action={
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Button
                  size="small"
                  color="inherit"
                  onClick={() => navigate("/dashboard/billing")}
                >
                  Upgrade
                </Button>
                <IconButton
                  size="small"
                  color="inherit"
                  onClick={() => handleDismiss(alert.id)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            }
          >
            <AlertTitle sx={{ fontSize: "0.8125rem", mb: 0 }}>
              {label} — {pct}% used
            </AlertTitle>
            You're using {Number(alert.currentUsage)} of {Number(alert.limitValue)}{" "}
            {label.toLowerCase()}. Consider upgrading your plan.
          </Alert>
        );
      })}
    </Box>
  );
}
